import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';

const SHOW_AFTER = 420;
const RING_SIZE = 48;
const RING_STROKE = 2.5;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const GoToTop = () => {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const { pathname } = useLocation();
  const { t } = useLocale();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    let frame = 0;

    const updateState = () => {
      const scrollY = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

      setVisible(scrollY > SHOW_AFTER);
      setProgress(maxScroll > 0 ? Math.min(1, scrollY / maxScroll) : 0);
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateState();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateState();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  const handleClick = () => {
    window.history.replaceState(null, '', pathname);
    window.scrollTo({
      top: 0,
      behavior: reduceMotion ? 'instant' : 'smooth',
    });
  };

  const ringOffset = RING_CIRCUMFERENCE * (1 - progress);

  return (
    <AnimatePresence>
      {visible ? (
        <motion.button
          type="button"
          className="go-to-top"
          aria-label={t('scroll_top_aria')}
          title={t('scroll_top_aria')}
          onClick={handleClick}
          initial={reduceMotion ? false : { opacity: 0, y: 28, scale: 0.82, rotate: -8 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: 18, scale: 0.88, rotate: 4 }}
          transition={
            reduceMotion
              ? { duration: 0.15 }
              : { type: 'spring', stiffness: 380, damping: 24, mass: 0.85 }
          }
          whileHover={reduceMotion ? undefined : { y: -5, scale: 1.05 }}
          whileTap={reduceMotion ? undefined : { scale: 0.9, y: 0 }}
        >
          <svg
            className="go-to-top__ring"
            width={RING_SIZE}
            height={RING_SIZE}
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            aria-hidden="true"
          >
            <circle
              className="go-to-top__ring-track"
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
            />
            <circle
              className="go-to-top__ring-progress"
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={ringOffset}
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
            />
          </svg>

          <span className="go-to-top__icon" aria-hidden="true">
            <ArrowUp size={18} strokeWidth={2.25} />
          </span>
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
};

export default GoToTop;
